import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { setKey } from "../store/actions";
import keys from "../store/keys";
import adService from "../services/ad";
import categoryService from "../services/category";
import subCategoryService from "../services/sub-category";
import conditionService from "../services/condition";
import cityService from "../services/city";
import userService from "../services/user";
import imageService from "../services/image";
import { swalSuccess, swalError } from "../utils/swal";
import { useTranslation } from "react-i18next";

function OfficialProducts({ session, setKey }) {
    const { t } = useTranslation();
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [conditions, setConditions] = useState([]);
    const [cities, setCities] = useState([]);
    const [officialUsers, setOfficialUsers] = useState([]);

    const [form, setForm] = useState({
        userId: "",
        title: "",
        description: "",
        categoryId: "",
        subCategoryId: "",
        conditionId: "",
        price: "",
        price: "",
        cityId: "",
        isWholesale: false,
        wholesalePrice: "",
        wholesaleMinQuantity: 1,
        coordinates: [0, 0]
    });
    const [imageFiles, setImageFiles] = useState([]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setKey(keys.isLoading, true);
        try {
            // Load dropdown data
            const [cats, conds, citiesResp, users] = await Promise.all([
                categoryService.getAll(session.token, ""),
                conditionService.getAll(session.token),
                cityService.getAll(session.token, ""),
                // Widen range to ensure we capture all users
                userService.getAll(session.token, "", "2000-01-01", "2100-01-01")
            ]);

            if (cats.data) setCategories(cats.data);
            if (conds.data) setConditions(conds.data);
            if (citiesResp.data) setCities(citiesResp.data);

            // Filter for Official Users
            if (users.data) {
                console.log("All Users:", users.data.length);
                const officials = users.data.filter(u => u.accountType === 'official' && u.active);
                console.log("Official Users:", officials);
                setOfficialUsers(officials);
                if (officials.length > 0) setForm(f => ({ ...f, userId: officials[0]._id }));
            }
        } catch (e) {
            console.error(e);
        }
        setKey(keys.isLoading, false);
    };

    const handleCategoryChange = async (e) => {
        const catId = e.target.value;
        setForm({ ...form, categoryId: catId, subCategoryId: "" });
        if (catId) {
            const subs = await subCategoryService.getByCategoryId(session.token, catId);
            if (subs.data) setSubCategories(subs.data);
        } else {
            setSubCategories([]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setKey(keys.isLoading, true);

        // 1. Create Ad
        const adResult = await adService.createOfficialAd(session.token, form);
        if (adResult.error) {
            swalError(adResult.error);
            setKey(keys.isLoading, false);
            return;
        }

        const adId = adResult.data._id;

        // 2. Upload Images
        if (imageFiles.length > 0) {
            for (let i = 0; i < imageFiles.length; i++) {
                const formData = {
                    adId: adId,
                    userId: form.userId,
                    file: imageFiles[i]
                };
                // Note: We need to adapt imageService to accept File object if it doesn't already.
                // admin/src/services/image.js: create uses FormData loop.
                // We'll pass the file directly to the fd.
                await imageService.create(session.token, formData);
            }
        }

        swalSuccess(t("official_product_posted"));
        setKey(keys.isLoading, false);
        // Reset or redirect?
        window.location.reload();
    };

    return (
        <div className="container mt-4">
            <h3>{t("post_official_product")}</h3>
            <div className="card shadow-sm p-4">
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>{t("select_official_account")}</label>
                        <select
                            className="form-control"
                            required
                            value={form.userId}
                            onChange={e => setForm({ ...form, userId: e.target.value })}
                        >
                            <option value="">{t("select_user_opt")}</option>
                            {officialUsers.map(u => (
                                <option key={u._id} value={u._id}>{u.name} ({u.email})</option>
                            ))}
                        </select>
                        {officialUsers.length === 0 && <small className="text-danger">{t("no_official_accounts")}</small>}
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label>{t("category")}</label>
                                <select className="form-control" required value={form.categoryId} onChange={handleCategoryChange}>
                                    <option value="">{t("select_category_opt")}</option>
                                    {categories.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <label>{t("sub_category")}</label>
                                <select className="form-control" value={form.subCategoryId} onChange={e => setForm({ ...form, subCategoryId: e.target.value })}>
                                    <option value="">{t("select_sub_category_opt")}</option>
                                    {subCategories.map(s => <option key={s._id} value={s._id}>{s.title}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>{t("col_title")}</label>
                        <input className="form-control" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
                    </div>

                    <div className="form-group">
                        <label>{t("col_description")}</label>
                        <textarea className="form-control" rows="4" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}></textarea>
                    </div>

                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label>{t("price_iqd")}</label>
                                <input type="number" className="form-control" required value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
                            </div>
                        </div>
                    </div>

                    <div className="card bg-light p-3 mb-3">
                        <div className="form-check mb-3">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="chkWholesale"
                                checked={form.isWholesale}
                                onChange={e => setForm({ ...form, isWholesale: e.target.checked })}
                            />
                            <label className="form-check-label font-weight-bold" htmlFor="chkWholesale">{t("enable_wholesale")}</label>
                        </div>

                        {form.isWholesale && (
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>{t("bulk_price_iqd")}</label>
                                        <input type="number" className="form-control" required={form.isWholesale} value={form.wholesalePrice} onChange={e => setForm({ ...form, wholesalePrice: e.target.value })} />
                                        <small className="text-muted">{t("bulk_price_help")}</small>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>{t("min_quantity")}</label>
                                        <input type="number" className="form-control" required={form.isWholesale} value={form.wholesaleMinQuantity} onChange={e => setForm({ ...form, wholesaleMinQuantity: e.target.value })} />
                                        <small className="text-muted">{t("min_quantity_help")}</small>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label>{t("col_condition")}</label>
                                <select className="form-control" required value={form.conditionId} onChange={e => setForm({ ...form, conditionId: e.target.value })}>
                                    <option value="">{t("select_condition_opt")}</option>
                                    {conditions.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="form-group">
                                <label>{t("col_city")}</label>
                                <select className="form-control" required value={form.cityId} onChange={e => setForm({ ...form, cityId: e.target.value })}>
                                    <option value="">{t("select_city_opt")}</option>
                                    {cities.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>{t("label_images")}</label>
                        <input type="file" className="form-control-file" multiple onChange={e => setImageFiles(e.target.files)} />
                        <small className="text-muted">{t("select_multiple_files")}</small>
                    </div>

                    <button type="submit" className="btn btn-primary btn-block">{t("post_official_product")}</button>
                </form>
            </div>
        </div>
    );
}

const mapStateToProps = store => ({ session: store.session });
const mapDispatchToProps = dispatch => ({ setKey: (key, value) => dispatch(setKey(key, value)) });
export default connect(mapStateToProps, mapDispatchToProps)(OfficialProducts);
